import { Badge } from 'components/Badge'
import { Button } from 'components/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/Dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/DropdownMenu'
import { Input } from 'components/Input'
import { Label } from 'components/Label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/Select'
import { Loader2, MoreHorizontal, Trash2, UserPlus, Users } from 'lucide-react'
import { selectCurrentUserId } from 'modules/common/selectors'
import { useAppSelector } from 'modules/core'
import type { BusinessMembershipEntity } from 'openapi/models/BusinessMembershipEntity'
import { BusinessMembersService } from 'openapi/services/BusinessMembersService'
import { useCallback, useEffect, useState } from 'react'
import { useIsMobile } from 'hooks'
import { MobilePageHeader } from '../components/MobilePageHeader'

type Role = 'admin' | 'member' | 'viewer'

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
}

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  active: 'secondary',
  invited: 'outline',
  suspended: 'destructive',
}

function getInitials(name?: string, email?: string) {
  const source = name || email || '?'
  return source
    .split(/[\s@]/)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase()
}

export function CollaboratorsPage() {
  const currentUserId = useAppSelector(selectCurrentUserId)
  const [members, setMembers] = useState<BusinessMembershipEntity[]>([])
  const [loading, setLoading] = useState(true)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<Role>('member')
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState('')
  const isMobile = useIsMobile()
  const [removingId, setRemovingId] = useState<string | null>(null)

  const fetchMembers = useCallback(async () => {
    try {
      const response = await BusinessMembersService.businessMembershipsControllerFindAllV1()
      setMembers(response.data)
    } catch (err) {
      console.error('Failed to fetch members:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  const handleInvite = async () => {
    const email = inviteEmail.trim()
    if (!email) return
    // Same shape check as LoginForm; catches typos before a 400 whose raw
    // class-validator string ("email must be an email") used to render as-is.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setInviteError('Please enter a valid email address.')
      return
    }
    setInviting(true)
    setInviteError('')
    try {
      await BusinessMembersService.businessMembershipsControllerInviteV1({
        email,
        role: inviteRole,
      })
      setInviteOpen(false)
      setInviteEmail('')
      setInviteRole('member')
      fetchMembers()
    } catch (err: any) {
      const raw = err?.body?.message
      const message =
        (Array.isArray(raw) ? raw.join(' ') : raw) || err?.message || 'Failed to send invitation'
      setInviteError(message)
    } finally {
      setInviting(false)
    }
  }

  const handleRemove = async (memberId: string) => {
    setRemovingId(memberId)
    try {
      await BusinessMembersService.businessMembershipsControllerRemoveV1(memberId)
      setMembers(prev => prev.filter(m => m.id !== memberId))
    } catch (err) {
      console.error('Failed to remove member:', err)
    } finally {
      setRemovingId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-paper-faint" />
      </div>
    )
  }

  return (
    <div
      className={
        isMobile
          ? 'flex h-full flex-col overflow-y-auto pb-6'
          : 'mx-auto w-full max-w-[840px] px-6 py-8'
      }
    >
      {/* Header — instant on mobile (this page had none, so at 390 the route
          rendered headerless until content landed). */}
      {isMobile ? (
        <MobilePageHeader
          title="Collaborators"
          subtitle="Manage your team members and their access."
          actions={
            <Button variant="t3-primary" size="t3-sm" onClick={() => setInviteOpen(true)}>
              <UserPlus size={14} />
              Invite
            </Button>
          }
        />
      ) : (
        <div className="mb-6 flex items-center justify-between">
          <div>
            <span className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
              Team
            </span>
            <h1 className="mt-1 text-[24px] font-semibold tracking-[-0.01em] text-paper sm:text-[28px]">
              Collaborators
            </h1>
            <p className="mt-1 text-[14px] text-paper-dim">
              Manage your team members and their access.
            </p>
          </div>
          <Button variant="t3-primary" size="t3-md" onClick={() => setInviteOpen(true)}>
            <UserPlus size={16} />
            Invite
          </Button>
        </div>
      )}

      {/* Members List */}
      {members.length === 0 ? (
        <div
          className={`flex flex-col items-center justify-center rounded-[12px] border border-dashed border-line bg-bg-2 py-16 ${isMobile ? 'mx-4 mt-4' : ''}`}
        >
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full border border-line-bright bg-bg-3 text-acid">
            <Users size={20} />
          </div>
          <p className="text-[14px] font-medium text-paper">No team members yet.</p>
          <p className="mt-1 text-[13px] text-paper-dim">Invite collaborators to get started.</p>
        </div>
      ) : (
        <div
          className={`overflow-x-auto rounded-[12px] border border-line bg-bg-2 ${isMobile ? 'mx-4 mt-4' : ''}`}
        >
          {/* min-w keeps the 4 columns readable at 390px and lets the card
              scroll horizontally; the old overflow-hidden clipped Role/Status
              and the actions menu off-screen on mobile (no way to revoke). */}
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="border-b border-line bg-bg-3">
                <th className="mono px-4 py-3 text-left text-[10px] tracking-[0.18em] text-paper-faint uppercase">
                  Member
                </th>
                <th className="mono px-4 py-3 text-left text-[10px] tracking-[0.18em] text-paper-faint uppercase">
                  Role
                </th>
                <th className="mono px-4 py-3 text-left text-[10px] tracking-[0.18em] text-paper-faint uppercase">
                  Status
                </th>
                <th className="w-10 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {members.map(member => {
                // A pending email-only invite has no linked user yet, so fall
                // back to the invited email for the name + avatar initials.
                const displayEmail = member.user?.email ?? member.invitedEmail ?? undefined
                return (
                  <tr key={member.id} className="transition-colors hover:bg-bg-3/60">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-line-bright bg-bg-3 text-[10px] font-semibold text-paper-dim">
                          {getInitials(member.user?.name, displayEmail)}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-[13px] font-medium text-paper">
                            {member.user?.name || displayEmail}
                            {member.userId === currentUserId && (
                              <span className="ml-1.5 text-xs text-paper-faint">(you)</span>
                            )}
                          </p>
                          {member.user?.name && (
                            <p className="truncate text-xs text-paper-dim">{member.user.email}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-paper">{ROLE_LABELS[member.role]}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_VARIANT[member.status] || 'outline'}>
                        {member.status === 'active'
                          ? 'Active'
                          : member.status === 'invited'
                            ? 'Invited'
                            : 'Suspended'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {member.role !== 'owner' && member.userId !== currentUserId && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              aria-label="Member actions"
                              className="rounded-[6px] p-1 text-paper-faint transition-colors hover:bg-bg-3 hover:text-paper"
                            >
                              <MoreHorizontal size={16} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              className="cursor-pointer text-[color:var(--alert-error-text)] focus:text-[color:var(--alert-error-text)]"
                              disabled={removingId === member.id}
                              onClick={() => handleRemove(member.id)}
                            >
                              <Trash2 size={14} />
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent size="sm" variant="dark">
          <DialogHeader>
            <DialogTitle variant="dark">Invite Team Member</DialogTitle>
            <DialogDescription variant="dark">
              Send an invitation email to add a collaborator to your business.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Email address</Label>
              <Input
                variant="dark"
                type="email"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={e => {
                  setInviteEmail(e.target.value)
                  setInviteError('')
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleInvite()
                }}
              />
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={inviteRole} onValueChange={v => setInviteRole(v as Role)}>
                <SelectTrigger variant="dark" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent variant="dark">
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {inviteError && (
              <p className="text-sm text-[color:var(--alert-error-text)]">{inviteError}</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="t3-tertiary" size="t3-md" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="t3-primary"
              size="t3-md"
              onClick={handleInvite}
              disabled={inviting || !inviteEmail.trim()}
            >
              {inviting && <Loader2 size={16} className="animate-spin" />}
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
